/* eslint-disable jsx-a11y/alt-text */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useResizableStore,
  createComponentAs,
  findNodePath,
  focusEditor,
  getNodeString,
  getPointAfter,
  setNodes,
  useComposedRef,
  useEditorRef,
  useElement,
  useCaptionString,
  captionGlobalStore,
} from '@udecode/plate';
import { useReadOnly, useSelected } from 'slate-react';
import isHotkey from 'is-hotkey';
import { Path } from 'slate';
import clsx from 'clsx';

import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';

export const useCaption = ({
  readOnly,
  ...props
} = {}) => {
  const width = useResizableStore().get.width();

  return {
    style: { width },
    ...props,
  };
};

export const useCaptionState = (props) => {
  const captionString = useCaptionString();

  const selected = useSelected();
  const _readOnly = useReadOnly();
  const readOnly = props.readOnly || _readOnly;

  return {
    captionString,
    selected,
    readOnly,
  };
};

export const CaptionRoot = createComponentAs((props) => {
  const htmlProps = useCaption(props);
  const { captionString, selected, readOnly } = useCaptionState(props);

  if (!captionString.length && (readOnly || !selected)) {
    return null;
  }

  return (
    <figcaption
      {...htmlProps}
    />
  );
});

/**
 * Focus textareaRef when focusCaptionPath is set to the image path.
 */
export const useCaptionTextareaFocus = (textareaRef) => {
  const editor = useEditorRef();
  const element = useElement();

  const focusCaptionPath = captionGlobalStore.use.focusEndCaptionPath();

  useEffect(() => {
    if (focusCaptionPath && textareaRef.current) {
      const path = findNodePath(editor, element);
      if (path && Path.equals(path, focusCaptionPath)) {
        textareaRef.current.focus();
        captionGlobalStore.set.focusEndCaptionPath(null);
      }
    }
  }, [editor, element, focusCaptionPath, textareaRef]);
};

export const useCaptionTextarea = (props) => {
  const element = useElement();

  const {
    caption: nodeCaption = [{ children: [{ text: '' }] }],
  } = element;

  const [captionValue, setCaptionValue] = useState(getNodeString(nodeCaption[0]));

  const editor = useEditorRef();
  const readOnly = useReadOnly();

  const textareaRef = useRef(null);
  const ref = useComposedRef(textareaRef, props.ref);

  useCaptionTextareaFocus(textareaRef);

  const onChange = useCallback(
    (e) => {
      const newValue = e.target.value;

      // local state
      setCaptionValue(newValue);

      const path = findNodePath(editor, element);
      if (!path) return;

      // saved state
      setNodes(
        editor,
        { caption: [{ text: newValue }] },
        { at: path },
      );
    },
    [editor, element],
  );

  const onKeyDown = (e) => {
    // select image
    if (isHotkey('up', e)) {
      const path = findNodePath(editor, element);
      if (!path) return;

      e.preventDefault();

      focusEditor(editor, path);
    }

    // select next block
    if (isHotkey('down', e)) {
      const path = findNodePath(editor, element);
      if (!path) return;

      const nextNodePath = getPointAfter(editor, path);
      if (!nextNodePath) return;

      e.preventDefault();

      focusEditor(editor, nextNodePath);
    }
  };

  return {
    value: captionValue,
    readOnly,
    onChange,
    onKeyDown,
    rows: 1,
    ...props,
    ref,
  };
};

export const CaptionTextarea = createComponentAs(
  (props) => {
    const { textareaRef } = useAutosizeTextarea({ enterToSubmit: true });
    const { className, ...rest } = props || {};
    const htmlProps = useCaptionTextarea(rest);
    htmlProps.className = clsx(
      className,
      'slate-ui-media__caption-text',
    );

    return (
      <textarea
        ref={textareaRef}
        {...htmlProps}
      />
    );
  },
);